import { call, put, takeLatest } from 'redux-saga/effects';
import { YieldOf } from 'types/utils';
import { Constants } from 'core';

import {
  createHelpInfo,
  createdHelpInfo,
  updateHelpInfo,
  updatedHelpInfo,
  deleteHelpInfo,
  deletedHelpInfo,
} from './actions';
import { fetchHelpInfos } from '../help-infos/actions';
import { logout } from '../api-auth/actions';
import getAPI from '../../api/iv-back';
import { sagaWithCallback } from '../utils';

type CreateHelpInfoActionPayload = ReturnType<typeof createHelpInfo>;
type UpdateHelpInfoActionPayload = ReturnType<typeof updateHelpInfo>;
type DeleteHelpInfoActionPayload = ReturnType<typeof deleteHelpInfo>;

export function* createHelpInfoSaga({ payload }: CreateHelpInfoActionPayload) {
  const { createHelpInfoApi } = getAPI();
  const response: YieldOf<typeof createHelpInfoApi> = yield call(createHelpInfoApi, payload);
  const { data, ok, problem, status } = response;
  if (status === 401 || status === 403) {
    yield put(logout());
  }
  if (ok) {
    yield put(fetchHelpInfos());
    yield put(createdHelpInfo(data));
    return data;
  } else {
    const error =
      status === Constants.ALREADY_EXISTED_STATUS ? Constants.ALREADY_EXISTED_PROBLEM_CODE : problem || 'unknown';
    yield put(createdHelpInfo(undefined, error));
    throw new Error('create help info failed: ' + error);
  }
}

export function* updateHelpInfoSaga({ payload }: UpdateHelpInfoActionPayload) {
  const { updateHelpInfoApi } = getAPI();
  const response: YieldOf<typeof updateHelpInfoApi> = yield call(updateHelpInfoApi, payload);
  const { data, ok, problem, status } = response;
  if (status === 401 || status === 403) {
    yield put(logout());
  }
  if (ok) {
    yield put(updatedHelpInfo(data));
    return data;
  } else {
    const error =
      status === Constants.ALREADY_EXISTED_STATUS ? Constants.ALREADY_EXISTED_PROBLEM_CODE : problem || 'unknown';
    yield put(updatedHelpInfo(undefined, error));
    throw new Error('update help info failed: ' + error);
  }
}

export function* deleteHelpInfoSaga({ payload: { id, filterValues } }: DeleteHelpInfoActionPayload) {
  const { deleteHelpInfoApi } = getAPI();
  const response: YieldOf<typeof deleteHelpInfoApi> = yield call(deleteHelpInfoApi, id);
  const { ok, problem, status } = response;
  if (status === 401 || status === 403) {
    yield put(logout());
  }
  if (ok) {
    yield put(filterValues ? fetchHelpInfos(filterValues) : fetchHelpInfos());
    yield put(deletedHelpInfo(true));
  } else {
    const error = problem || 'unknown';
    yield put(deletedHelpInfo(false, error));
  }
}

export default function* helpInfosSaga() {
  yield takeLatest(createHelpInfo.type, sagaWithCallback(createHelpInfoSaga));
  yield takeLatest(updateHelpInfo.type, sagaWithCallback(updateHelpInfoSaga));
  yield takeLatest(deleteHelpInfo.type, deleteHelpInfoSaga);
}
